import { Injectable } from '@angular/core';
import { UserService } from './user.service';

declare let gtag: Function;

export interface GA4EventParams {
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class GA4Service {
  private readonly GA4_MEASUREMENT_ID = 'G-XMZLTRKB12';

  constructor(private userService: UserService) {}

  private isGA4Available(): boolean {
    return typeof gtag !== 'undefined';
  }

  private isAdminUser(): boolean {
    const user = this.userService.user;

    const userType = (user as any)?.userType;
    if (!userType) {
      return false;
    }

    return String(userType).toLowerCase() === 'adm';
  }


  trackEvent(eventName: string, params: GA4EventParams = {}): void {
    if (this.isAdminUser()) {
      return;
    }

    if (!this.isGA4Available()) {
      console.warn('GA4 não está disponível. Evento não enviado:', eventName, params);
      return;
    }

    const finalParams: GA4EventParams = {};
    for (const key in params) {
      if (params.hasOwnProperty(key) && params[key] !== undefined && params[key] !== null) {
        finalParams[key] = params[key];
      }
    }

    try {
      gtag('event', eventName, finalParams);
    } catch (error) {
      console.error('[GA4] Erro ao enviar evento:', eventName, error);
    }
  }

  trackScreenView(screenName: string, featureName: string, additionalParams: GA4EventParams = {}): void {
    const params: GA4EventParams = {
      screen_name: screenName,
      feature_name: featureName,
      ...additionalParams
    };
    this.trackEvent('screen_view', params);
  }

  trackApiError(api: string, errorCode: string | number, screenName: string, featureName: string): void {
    this.trackEvent('api_error', {
      api,
      error_code: String(errorCode),
      screen_name: screenName,
      feature_name: featureName,
      has_error: true
    });
  }

  trackFormError(field: string, screenName: string, featureName: string, errorType?: string): void {
    this.trackEvent('form_error', {
      field,
      error_type: errorType,
      screen_name: screenName,
      feature_name: featureName,
      has_error: true
    });
  }

  trackUserAccess(): void {
    if (this.isAdminUser()) {
      return;
    }

    if (!this.isGA4Available()) {
      console.warn('GA4 não está disponível. Acesso do usuário não registrado.');
      return;
    }

    const contract = this.userService.getCurrentContract();

    if (!contract || !contract.id) {
      console.warn('GA4: Contrato não encontrado. Acesso não registrado.');
      return;
    }

    try {
      const user = this.userService.user;

      if (user?.id) {
        gtag('config', this.GA4_MEASUREMENT_ID, {
          user_id: String(user.id)
        });
      }

      const userParams: GA4EventParams = {
        app_module_group: 'Nexus',
        app_module: 'nexus',
        contract_id: contract.id,
        contract_name: contract.name,
      };

      if (user?.email) {
        userParams['user_email'] = user.email;
      }

      if (user?.id) {
        userParams['app_user_id'] = String(user.id);
      }

      gtag('set', 'user_properties', {
        contract_id: contract.id,
        contract_name: contract.name,
        app_module: 'nexus'
      });

      gtag('event', 'user_access', userParams);
    } catch (error) {
      console.error('Erro ao registrar acesso do usuário:', error);
    }
  }

  // Rastreia usuários ativos por tela
  trackActiveUserByScreen(screenName: string, featureName: string): void {
    if (this.isAdminUser()) {
      return;
    }

    if (!this.isGA4Available()) {
      console.warn('GA4 não está disponível. Rastreamento de tela não registrado.');
      return;
    }

    const contract = this.userService.getCurrentContract();

    if (!contract || !contract.id) {
      console.warn('GA4: Contrato não encontrado. Rastreamento de tela não registrado.');
      return;
    }

    try {
      const user = this.userService.user;

      const params: GA4EventParams = {
        screen_name: screenName,
        feature_name: featureName,
        contract_id: contract.id,
        contract_name: contract.name,
        app_module: 'nexus'
      };

      if (user?.id) {
        params['user_id'] = String(user.id);
      }

      this.trackEvent('active_user_by_screen', params);
    } catch (error) {
      console.error('Erro ao registrar usuário ativo por tela no GA4:', error);
    }
  }
}

